export const useEnrichmentStore = defineStore('enrichment', () => {
  const overlays = useOverlays()
  const guards = useGuards()

  const acceptedFormats = ref<string[]>(['text/csv'])
  const dragCounter = ref(0)
  const dragFilesCount = ref(0)
  const dropzoneTargets = ref<HTMLElement[]>([])
  const fileInputKey = ref(0)
  const fileInputRef = ref<HTMLInputElement>()
  const fileToImport = ref<File>()
  const formatError = ref(false)
  const initializing = ref(false)
  const isDroppingFile = ref(false)
  const isSelectingFile = ref(false)
  const maxCompanies = ref(100 * 1000)

  const formattedFormats = computed(() => {
    return acceptedFormats.value
      .map(accept => !accept.includes('*') ? accept.replace(/image|text|video|\.|\//g, '') : accept)
      .join(',')
  })

  // Methods
  function reset() {
    // Reset all input elements
    dropzoneTargets.value.forEach((target) => {
      // Handle input elements
      if (target instanceof HTMLInputElement) {
        target.value = ''
      }
      // Handle file input elements specifically
      if (target instanceof HTMLInputElement && target.type === 'file') {
        target.files = null
      }
    })

    // Reset file input ref if it exists
    if (fileInputRef.value) {
      fileInputRef.value.value = ''
      // Clear the files property
      fileInputRef.value.files = null
      // Force Vue to re-render the input
      fileInputKey.value++
    }

    // Reset state
    fileToImport.value = undefined
    formatError.value = false
    dragFilesCount.value = 0
    isDroppingFile.value = false
    isSelectingFile.value = false
    initializing.value = false
    dragCounter.value = 0
  }

  function addDropzoneTarget(element: HTMLElement) {
    // Check if element already exists in the array
    if (!dropzoneTargets.value.includes(element)) {
      // If this is the first target being added, register event listeners
      const shouldRegisterListeners = dropzoneTargets.value.length === 0

      // Add the target
      dropzoneTargets.value.push(element)

      // Register event listeners if this is the first target
      if (shouldRegisterListeners) {
        registerEventListeners()
      }
    }
  }

  function removeDropzoneTarget(element: HTMLElement) {
    const index = dropzoneTargets.value.indexOf(element)

    if (index !== -1) {
      dropzoneTargets.value.splice(index, 1)

      // If no targets left, unregister event listeners
      if (dropzoneTargets.value.length === 0) {
        unregisterEventListeners()
      }
    }
  }

  function clearDropzoneTargets() {
    // Unregister event listeners
    if (dropzoneTargets.value.length > 0) {
      unregisterEventListeners()
    }

    // Clear all targets
    dropzoneTargets.value = []
  }

  function validateFileFormat(file: File): boolean {
    return acceptedFormats.value.includes(file.type)
  }

  function handleFileDrop(files: FileList) {
    dragFilesCount.value = files.length
    formatError.value = false

    // Get first file (we don't support multiple files currently)
    const file = files[0]

    // Validate format
    if (!validateFileFormat(file)) {
      formatError.value = true

      return
    }

    // Set the file to import
    fileToImport.value = file

    if (fileToImport.value) {
      overlays.toggleModal({
        onClose: reset,
        opened: true,
        type: 'listEnrichment',
      })
    }

    return file
  }

  // Helper to check if the drag event contains files
  const containsFiles = (event: DragEvent): boolean => {
    if (!event.dataTransfer) {
      return false
    }

    // Check types
    if (event.dataTransfer.types) {
      for (const type of event.dataTransfer.types) {
        if (type === 'Files' || type === 'application/x-moz-file') {
          return true
        }
      }
    }

    // Also check items if available
    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      for (const item of event.dataTransfer.items) {
        if (item.kind === 'file') {
          return true
        }
      }
    }

    return false
  }

  const handleDragEnter = (event: DragEvent) => {
    event.preventDefault()

    // Only count it if it contains files
    if (containsFiles(event)) {
      dragCounter.value++
      isDroppingFile.value = true
    }
  }

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault()

    // Make sure we maintain the dropping state
    // Some elements can cause dragLeave to fire incorrectly
    if (containsFiles(event) && !isDroppingFile.value) {
      isDroppingFile.value = true
    }
  }

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault()

    // Only decrease counter if it contains files
    if (containsFiles(event)) {
      dragCounter.value--

      // Only set isDroppingFile to false if we've left the document
      // This prevents flickering when hovering over child elements
      if (dragCounter.value <= 0) {
        dragCounter.value = 0
        isDroppingFile.value = false
      }
    }
  }

  const handleDrop = async (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const files = event.dataTransfer?.files

    const onSuccess = () => {
      // Reset counter and dropping state
      dragCounter.value = 0
      isDroppingFile.value = false

      // Only handle the drop if there are drop targets and data
      if (!dropzoneTargets.value.length || !files?.length) {
        return
      }

      // Check if drop is on any of the target elements
      const x = event.clientX || 0
      const y = event.clientY || 0
      const elementAtPoint = document.elementFromPoint(x, y)

      // Check if the element at point is contained in any of our dropzone targets
      const isTargetDrop = dropzoneTargets.value.some(target => elementAtPoint && target.contains(elementAtPoint))

      if (isTargetDrop && files) {
        // Wait until the modal animation is complete
        handleFileDrop(files)
      }
    }

    await guards.awaitUser({
      onClose: reset,
      onSuccess,
    })
  }

  const registerEventListeners = () => {
    document.addEventListener('dragenter', handleDragEnter, { passive: false })
    document.addEventListener('dragover', handleDragOver, { passive: false })
    document.addEventListener('dragleave', handleDragLeave, { passive: false })
    document.addEventListener('drop', handleDrop, { passive: false })
  }

  const unregisterEventListeners = () => {
    document.removeEventListener('dragenter', handleDragEnter)
    document.removeEventListener('dragover', handleDragOver)
    document.removeEventListener('dragleave', handleDragLeave)
    document.removeEventListener('drop', handleDrop)
  }

  const bindDropzoneRef = (dropzoneRef: Ref<HTMLElement | null>) => {
    onMounted(() => {
      if (dropzoneRef.value) {
        addDropzoneTarget(dropzoneRef.value as HTMLElement)
      }
    })

    onBeforeUnmount(() => {
      if (dropzoneRef.value) {
        removeDropzoneTarget(dropzoneRef.value as HTMLElement)
      }
    })

    // Watch for dropzone ref changes
    watch(
      () => dropzoneRef.value,
      (value) => {
        if (value) {
          addDropzoneTarget(value as HTMLElement)
        }
      },
    )
  }

  const handleInputClick = () => {
    if (fileInputRef.value) {
      fileInputRef.value.click()
    }
  }

  return {
    acceptedFormats,
    addDropzoneTarget,
    bindDropzoneRef,
    clearDropzoneTargets,
    dragFilesCount,
    dropzoneTargets,
    fileInputKey,
    fileInputRef,
    fileToImport,
    formatError,
    formattedFormats,
    handleFileDrop,
    handleInputClick,
    initializing,
    isDroppingFile,
    isSelectingFile,
    maxCompanies,
    removeDropzoneTarget,
    reset,
  }
})
